import { useEffect, useState } from "react";
import { IGetUserFlowByUrlUserSteps, useGetUserFlowByUrl } from "../useGetUserFlowByUrl";
import { getFeedbackStepIds, getParentModuleIds, getSummaryStepIds } from "@/utils";
import { useGroups } from "../useGroups";
import { CONSTANTS } from "@/constants";
import { gql } from '@apollo/client';
import { IIsLoading } from "@netfront/ui-library";
import { IApolloQuery, PartialPick } from "@netfront/common-library";

interface IUseGetModulesPayload extends IIsLoading {
  feedbackSteps: IGetUserFlowByUrlUserSteps[];
  modules: IGetUserFlowByUrlUserSteps[];
  summaryModules: IGetUserFlowByUrlUserSteps[];
};

interface IUseGetModulesOptions extends PartialPick<IApolloQuery, 'query'> {}

export const useGetModules = (options?: IUseGetModulesOptions): IUseGetModulesPayload => {
  const { query } = options ?? {} as IUseGetModulesOptions;
  const { hasStressManagement } = useGroups();

  const [modules, setModules] = useState<IGetUserFlowByUrlUserSteps[]>([]);
  const [summaryModules, setSummaryModules] = useState<IGetUserFlowByUrlUserSteps[]>([]);
  const [feedbackSteps, setFeedbackSteps] = useState<IGetUserFlowByUrlUserSteps[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { handleGetUserFlowByUrl } = useGetUserFlowByUrl({
    onCompleted: ({ userFlow: { userFlowSteps }}) => {
      if(!userFlowSteps) return;
      setModules(
        [...userFlowSteps].filter(({ id }) => getParentModuleIds()
          .includes(id))
          .filter(step => 
            hasStressManagement || 
            step.id !== CONSTANTS.USERFLOW.PARENT.STEPS.MODULES.COPING_WITH_STRESS.ID
          )
          .sort((a, b) => a.contentGroup.sort - b.contentGroup.sort)
      );

      setSummaryModules(
        [...userFlowSteps].filter(({ id }) => getSummaryStepIds()
          .includes(id))
          .sort((a, b) => a.contentGroup.sort - b.contentGroup.sort)
      );

      setFeedbackSteps(
        [...userFlowSteps].filter(({ id }) => getFeedbackStepIds()
          .includes(id))
          .sort((a, b) => a.contentGroup.sort - b.contentGroup.sort)
      );

      setIsLoading(false);
    },
    onError: () => {
      setIsLoading(false);
    },
    query: query ?? gql`
      query getUserFlowByUrl($projectId: String!, $url: String!) {
        userFlow {
          getUserFlowByUrl(projectId: $projectId, url: $url) {
            userFlowSteps {
              contentGroup {
                iconAsset {
                  presignedUrl
                }
                contentPages(status: [PUBLISHED]) {
                  hasBeenViewed
                  url
                  id
                  parentId
                  sort
                }
                sort
                title
              }
              id
              isComplete
              lastViewedContentPageCompletion {
                contentPage {
                  url
                }
              }
              lastTracking {
                completedDate
                startedLongDate
                id
              }
              percentageCompleted
              sort
              stepName
              url
            }
          }
        }
      }
    `
  });

  useEffect(() => {
    handleGetUserFlowByUrl({
      projectId: String(process.env.REACT_APP_PROJECT_ID),
      url: CONSTANTS.USERFLOW.PARENT.URL,
    });
  }, []);


  return {
    modules,
    feedbackSteps,
    summaryModules,
    isLoading
  }
}